// Aggregated display component for task-related metadata like projects, contexts, and dates.
// @ts-check
import { IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RescheduleIndicator from '@/features/tasks/components/atoms/RescheduleIndicator';
import {
  renderFixedTrailingColumns,
  resolveTaskMetadataProps,
} from '@/features/tasks/components/atoms/TaskMetadataFixedColumns';
import { getUrgencyColor } from '@/features/tasks/utils/ui/colorUtils';
import { translate as t } from '@/i18n/useT';
import { buildMetadataViewState } from '@/features/tasks/components/atoms/taskMetadataViewState';

/** @param {import('@mui/material/styles').Theme} theme */
function resolveUrgencyColorValue(theme, urgencyColor) {
  if (urgencyColor === 'error.main') return theme.palette.error.main;
  if (urgencyColor === 'warning.main') return theme.palette.warning.main;
  return 'inherit';
}

/** @param {{ show: boolean, formattedDate: string|null, isCompleted: boolean, urgencyColorValue: string, rescheduleCount?: number }} props */
function renderPlannedDate({
  show,
  formattedDate,
  isCompleted,
  urgencyColorValue,
  rescheduleCount,
}) {
  if (!show) return null;
  return (
    <span
      className="task-item__planned-date"
      data-testid="task-item__planned-date"
      style={{ color: isCompleted ? 'inherit' : urgencyColorValue }}
    >
      {formattedDate}
      {!isCompleted ? <RescheduleIndicator count={rescheduleCount} /> : null}
    </span>
  );
}

/** @param {{ hasConflict: boolean, onRebalance: (id: string) => void, taskId: string }} props */
function renderConflictButton({ hasConflict, onRebalance, taskId }) {
  if (!hasConflict) return null;
  return (
    <Tooltip title={t('taskmetadata.fixOrder', 'Fix Order')}>
      <IconButton
        color="error"
        size="small"
        aria-label={t('taskmetadata.fixOrder', 'Fix Order')}
        onClick={(e) => {
          e.stopPropagation();
          onRebalance(taskId);
        }}
      >
        <AutoFixHighIcon />
      </IconButton>
    </Tooltip>
  );
}

/** @param {{ caption: string|null }} props */
function renderOverdueCaption({ caption }) {
  if (!caption) return null;
  return <span className="task-item__overdue-caption">{caption}</span>;
}

/**
 * @param {{ progress: { scheduled: number, total: number } | null }} props
 */
function renderPartiallyScheduledMeta({ progress }) {
  if (!progress) return null;
  const label = t(
    'plan.partiallyScheduled',
    '{done} of {total} scheduled',
    { done: progress.scheduled, total: progress.total },
  );
  const compactLabel = `${progress.scheduled}/${progress.total}`;
  return (
    <span
      className="task-item__partially-scheduled"
      data-testid="task-item__partially-scheduled"
      aria-label={label}
      title={label}
    >
      {compactLabel}
    </span>
  );
}

/**
 * @param {{
 *   show: boolean,
 *   taskDate: string|null,
 *   formattedDate: string|null,
 *   urgencyColorValue: string,
 *   urgencyColor: string|null,
 *   rescheduleCount?: number,
 * }} props
 */
function renderDueDate({
  show,
  taskDate,
  formattedDate,
  urgencyColorValue,
  urgencyColor,
  rescheduleCount,
}) {
  if (!show || !taskDate) return null;
  return (
    <span
      className="task-date"
      title={t('taskmetadata.dueDate', 'Due date')}
      style={{ color: urgencyColorValue }}
      data-urgency-level={urgencyColor}
    >
      <CalendarTodayIcon
        sx={{ fontSize: '14px', marginRight: '4px', color: 'inherit' }}
      />
      {formattedDate}
      <RescheduleIndicator count={rescheduleCount} />
    </span>
  );
}

/** @param {Parameters<typeof resolveTaskMetadataProps>[0]} rawProps */
export const TaskMetadata = (rawProps) => {
  const {
    allProjects,
    projectDisplayMap,
    projectColorMap,
    allContexts,
    contextDisplayMap,
    hasConflict,
    onRebalance,
    taskId,
    taskDate,
    formattedDate,
    isOrganizeView,
    isMasterView,
    isExecutionView,
    isPlanView,
    isDashboardView,
    isDashboardSurface,
    isTodayUpcomingSurface,
    displayDuration,
    isRollup,
    isFocused,
    focusStartTime,
    estimatedDuration,
    rescheduleCount,
    isSubtaskView,
    isCompleted,
    listId,
    task,
    tasksMap,
  } = resolveTaskMetadataProps(rawProps);

  const theme = useTheme();
  const urgencyColor = taskDate ? getUrgencyColor(taskDate) : null;
  const urgencyColorValue = resolveUrgencyColorValue(theme, urgencyColor);
  const viewState = buildMetadataViewState({
    allProjects,
    taskDate,
    formattedDate,
    isExecutionView,
    isPlanView,
    isMasterView,
    isDashboardView,
    isDashboardSurface,
    isTodayUpcomingSurface,
    isSubtaskView,
    isCompleted,
    isOrganizeView,
    displayDuration,
    isFocused,
    focusStartTime,
    listId,
    task,
    tasksMap,
  });
  const hasPills =
    (viewState.visibleProjects?.length ?? 0) > 0 ||
    (!isExecutionView && (allContexts?.length ?? 0) > 0);

  return (
    <>
      {renderPlannedDate({
        show: viewState.showDateBeforePills,
        formattedDate,
        isCompleted,
        urgencyColorValue,
        rescheduleCount,
      })}
      {renderConflictButton({ hasConflict, onRebalance, taskId })}
      {renderOverdueCaption({ caption: viewState.overdueCaption })}
      {renderPartiallyScheduledMeta({
        progress: viewState.partiallyScheduledProgress,
      })}
      {renderDueDate({
        show: !viewState.hideNormalDateChrome,
        taskDate,
        formattedDate,
        urgencyColorValue,
        urgencyColor,
        rescheduleCount,
      })}
      {renderFixedTrailingColumns({
        show: hasPills || viewState.showDuration,
        visibleProjects: viewState.visibleProjects,
        projectDisplayMap,
        projectColorMap,
        allContexts,
        contextDisplayMap,
        denseChips: viewState.denseChips,
        isExecutionView,
        showDuration: viewState.showDuration,
        isFocused,
        focusStartTime,
        displayDuration,
        estimatedDuration,
        isRollup,
        showDurationIcon: viewState.showDurationIcon,
      })}
    </>
  );
};
